import { api } from 'api';
import { LogUtil } from 'components/common/LogUtil';
import {
  createOrder,
  createOrderAsGuest,
  updateOrder,
  updateOrderAsGuest,
} from 'components/fx/func';
import {
  FUNC_DEFAULT,
  FX_TRANSACTION_ADD_PREFIX,
  METHOD_DEPOSIT,
  METHOD_EPAYMENT,
  METHOD_PAYID,
  METHOD_PAYTO,
  SENDER_ID,
  STATUS_FAIL,
  STATUS_SUCCESS,
} from 'constants';
import { AS_GUEST } from 'constants/fx';
import usePaytoAPI from 'features/to/hooks/payment/usePaytoAPI';
import Cookies from 'helpers/cookies';
import { checkMaintenance, checkSystemError } from 'helpers/maintenance';
import useAuth from 'hooks/useAuth';
import useCommonStore from 'hooks/useCommonStore';
import useTxnSubmitError from 'hooks/useTxnSubmitError';
import { isEmpty } from 'lodash';
import useFxAddTransaction from './useFxAddTransaction';
import useFxFormAddTransactionStore from './useFxFormAddTransactionStore';

const useFxPayment = () => {
  const { token } = useAuth();

  const { setLoading, setPending, setLoadingMessage, paytoAccountInformation } =
    useCommonStore();
  const {
    paymentMethodName,
    addTransactionCurrentStep: currentStep,
    setAddTransactionCurrentStep,
    isDiscountApplied,
    setTokenTransaction,
    getPayloadToCreateOrderSubmit,
    confirmInformationEmail,
    isAdditionalInformationRequired,
    transactionDetails,
    setCreateTxnResponse,
    resetDenomination,
    resetNotes,
  } = useFxFormAddTransactionStore();
  const {
    isDisabled,
    isUpdateTran,
    isCreateNewTran,
    orderRef,
    isFxAsGuest,
    getFxTransactionInformation,
  } = useFxAddTransaction();
  const { payloadTransformFromPaytoAccountInformation } = usePaytoAPI();
  const { showTxnSubmitErrorModal } = useTxnSubmitError();

  const homeUrl = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;

  const handlePayProcess = async ({
    transactionInformation,
    setTransactionInformation,
    setShowFormPaymentByDeposit,
    setShowTransactionDetails,
    createTxnResponse,
  }) => {
    try {
      setTransactionInformation(transactionInformation);
      const { paymentMethod } = transactionInformation || {};
      const isShowPaymentInstruction = isEmpty(createTxnResponse)
        ? true
        : createTxnResponse?.showPaymentInstruction;
      if ((paymentMethod || paymentMethodName) === METHOD_EPAYMENT) {
        if (!isShowPaymentInstruction) {
          setAddTransactionCurrentStep(currentStep + 1);
        } else {
          setShowFormPaymentByDeposit(true);
        }
      } else {
        setAddTransactionCurrentStep(currentStep + 1);
      }
      setShowTransactionDetails(false);
      setLoading(false);
    } catch (error) {
      checkMaintenance(error);
      LogUtil.error('handlePayProcess', 'error', error);
    }
  };

  const checkPaymentPaytoInstruction = (
    data,
    onOpenPaytoErrorCodeModal,
    fetchPaytoAccountsAgreementLatest
  ) => {
    if (isEmpty(data?.paymentPaytoInstruction)) return false;
    if (!isFxAsGuest && paymentMethodName === METHOD_PAYTO) {
      const { errorCode, errorMessage } = data?.paymentPaytoInstruction || {};
      // {
      //   "status": "RJCT",
      //   "errorCode": "PTE_0200",
      //   "errorMessage": "Insufficient funds"
      // }
      if (errorCode || errorMessage) {
        setLoading(false);
        const errorData = {
          errorCode,
          message: errorMessage,
        };
        const failPath = `${homeUrl}${FX_TRANSACTION_ADD_PREFIX}?orderRef=${
          data?.orderRef || currentOrderRef
        }`;
        onOpenPaytoErrorCodeModal(
          errorData,
          '',
          '',
          FUNC_DEFAULT,
          '',
          {
            failPath: failPath,
          },
          payloadTransformFromPaytoAccountInformation(paytoAccountInformation)
        );
        setLoading(false);
        return true;
      }
    }
    return false;
  };
  const handleOnPay = async ({
    createTransactionPayload,
    transactionInformation,
    setTransactionInformation,
    setShowTransactionDetails,
    setShowFormPaymentByDeposit,
    setErrorCode,
    setShowDiscountInvalidPopup,
    onOpenPaytoErrorCodeModal,
    fetchPaytoAccountsAgreementLatest,
    resetThrottle,
  }) => {
    setLoading(true);

    const currentOrderRef = transactionInformation?.orderRef || orderRef;

    if (isUpdateTran) {
      LogUtil.log('isUpdateTran', 'start');
      const isChangePaymentMethod =
        (!isEmpty(transactionInformation)
          ? transactionInformation?.paymentMethod
          : transactionDetails?.paymentMethod) &&
        paymentMethodName &&
        (!isEmpty(transactionInformation)
          ? transactionInformation?.paymentMethod !== paymentMethodName
          : transactionDetails?.paymentMethod !== paymentMethodName);

      const isUpdateTranWithoutSubmit =
        isDisabled &&
        !isChangePaymentMethod &&
        (paymentMethodName === METHOD_PAYID ||
          paymentMethodName === METHOD_DEPOSIT);
      if (isUpdateTranWithoutSubmit) {
        LogUtil.log('isUpdateTranWithoutSubmit', 'start');
        setAddTransactionCurrentStep(currentStep + 1);
        setShowTransactionDetails(false);
        setLoading(false);
        return;
      }
      const originalResponseSurcharge = !isEmpty(transactionInformation)
        ? transactionInformation?.surcharge
        : transactionDetails?.surcharge;
      const shouldPersistSurchargeExclusion =
        Number(originalResponseSurcharge) > 0;

      const isUpdateTranWithoutSubmitByCC =
        isDisabled &&
        !isChangePaymentMethod &&
        paymentMethodName === METHOD_EPAYMENT &&
        !shouldPersistSurchargeExclusion;
      if (isUpdateTranWithoutSubmitByCC) {
        LogUtil.log('isUpdateTranWithoutSubmitByCC', 'start');
        try {
          const transactionInformation = await getFxTransactionInformation({
            token,
            orderRef: currentOrderRef,
            email: confirmInformationEmail,
          });
          if (!isEmpty(transactionInformation)) {
            handlePayProcess({
              transactionInformation,
              setTransactionInformation,
              setShowFormPaymentByDeposit,
              setShowTransactionDetails,
            });
          }
        } catch (error) {
          checkMaintenance(error);
          checkSystemError(error);
          console.error(error?.message);
          setLoading(false);
        }
        return;
      }

      try {
        const updateTransactionPayload = {
          ...createTransactionPayload,
          orderRef: currentOrderRef,
          ...(!isEmpty(transactionInformation?.customer) && {
            customer: transactionInformation?.customer,
          }),
        };
        console.log('updateTransactionPayload:::', updateTransactionPayload);

        const response = !isFxAsGuest
          ? await updateOrder(token, updateTransactionPayload)
          : await updateOrderAsGuest(updateTransactionPayload);
        const data = !isEmpty(response?.data) ? response?.data : response;
        if (!isEmpty(data)) {
          if (
            checkPaymentPaytoInstruction(
              data,
              onOpenPaytoErrorCodeModal,
              fetchPaytoAccountsAgreementLatest
            )
          )
            return;
          Cookies.set(SENDER_ID, data?.senderId);
          const transactionInformation = await getFxTransactionInformation({
            token,
            orderRef: data?.orderRef,
            email: confirmInformationEmail,
          });
          setCreateTxnResponse(data);
          handlePayProcess({
            transactionInformation,
            setTransactionInformation,
            setShowFormPaymentByDeposit,
            setShowTransactionDetails,
          });
        }
      } catch (error) {
        checkMaintenance(error);
        checkSystemError(error);
        console.error(error?.message);
        if (isDiscountApplied) {
          setErrorCode(error?.response?.data?.errorCode);
          setShowDiscountInvalidPopup(true);
        }
      } finally {
        resetThrottle();
      }
      return;
    }
    if (isCreateNewTran) {
      LogUtil.log('isCreateNewTran', 'start');
      try {
        if (isFxAsGuest) {
          setLoadingMessage('label_loading_processing_message');
        }
        const response = !isFxAsGuest
          ? await createOrder(token, createTransactionPayload)
          : await createOrderAsGuest(createTransactionPayload);
        setLoadingMessage();
        const data = !isEmpty(response?.data) ? response?.data : response;
        if (!isEmpty(data)) {
          if (
            checkPaymentPaytoInstruction(
              data,
              onOpenPaytoErrorCodeModal,
              fetchPaytoAccountsAgreementLatest
            )
          )
            return;
          Cookies.set(SENDER_ID, data?.senderId);
          const transactionInformation = await getFxTransactionInformation({
            token,
            orderRef: data?.orderRef || currentOrderRef,
            email: confirmInformationEmail,
          });
          setCreateTxnResponse(data);
          resetDenomination();
          resetNotes();
          handlePayProcess({
            transactionInformation: isEmpty(transactionInformation)
              ? data
              : transactionInformation,
            setTransactionInformation,
            setShowFormPaymentByDeposit,
            setShowTransactionDetails,
            createTxnResponse: data,
          });
          if (!isEmpty(transactionInformation)) {
            // handle save profile occupation
            if (!isFxAsGuest && isAdditionalInformationRequired) {
              const payloadOccupation = {
                job: transactionInformation.occupation,
                previousJob: transactionInformation.previousOccupation,
              };
              const { data: dataOccupation } = await api.getOccupation(token);
              if (!isEmpty(dataOccupation)) {
                const profileJob = dataOccupation?.job;
                const profilePrevJob = dataOccupation?.previousJob;

                const isChangeOccupation =
                  profileJob !== payloadOccupation?.job;
                const isChangePrevOccupation =
                  profilePrevJob &&
                  payloadOccupation?.previousJob &&
                  profilePrevJob !== payloadOccupation?.previousJob;
                if (
                  transactionInformation.occupation &&
                  (isChangeOccupation || isChangePrevOccupation)
                ) {
                  await api.saveOccupation(token, payloadOccupation);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error(
          `🔥 useFxPayment ❌ handleOnPay ➡️ error 1 ↩`,
          error?.message
        );
        if (!isFxAsGuest && error?.code === 'ERR_CANCELED') {
          showTxnSubmitErrorModal();
          return;
        }
        checkMaintenance(error);
        checkSystemError(error);
        setLoading(false);
        setLoadingMessage();
        if (isDiscountApplied) {
          setErrorCode(error?.response?.data?.errorCode);
          setShowDiscountInvalidPopup(true);
        }
      } finally {
        resetThrottle();
      }
      return;
    }

    try {
      const transactionInformation = await getFxTransactionInformation({
        token,
        orderRef: currentOrderRef,
        email: confirmInformationEmail,
      });
      if (!isEmpty(transactionInformation)) {
        handlePayProcess({
          transactionInformation,
          setTransactionInformation,
          setShowFormPaymentByDeposit,
          setShowTransactionDetails,
        });
      }
    } catch (error) {
      checkMaintenance(error);
      checkSystemError(error);
      console.error(
        `🔥 PaymentMethod ❌ handleOnPay ➡️ error 2 ↩`,
        error?.message
      );
      setLoading(false);
    } finally {
      resetThrottle();
    }
  };
  const handleOnPayProcess = async ({
    transactionInformation,
    setTransactionInformation,
    setShowTransactionDetails,
    setShowFormPaymentByDeposit,
    setErrorCode,
    setShowDiscountInvalidPopup,
    onOpenPaytoErrorCodeModal,
    fetchPaytoAccountsAgreementLatest,
    resetThrottle = FUNC_DEFAULT,
  }) => {
    const createTransactionPayload = getPayloadToCreateOrderSubmit();
    console.log('createTransactionPayload:::', createTransactionPayload);

    await handleOnPay({
      createTransactionPayload,
      transactionInformation,
      setTransactionInformation,
      setShowTransactionDetails,
      setShowFormPaymentByDeposit,
      setErrorCode,
      setShowDiscountInvalidPopup,
      onOpenPaytoErrorCodeModal,
      fetchPaytoAccountsAgreementLatest,
      resetThrottle,
    });
  };

  const handleOnPayByDeposit = async ({
    urlRedirect,
    transactionInformation,
    handleOnCloseFormPaymentByDeposit,
  }) => {
    const currentOrderRef = transactionInformation?.orderRef || orderRef;
    const email = transactionInformation?.customer?.email;
    const homeUrl = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
    const failPath = isFxAsGuest
      ? `${homeUrl}/fx-tran?as=${AS_GUEST}&orderRef=${currentOrderRef}&paymentStatus=${STATUS_FAIL}&email=${email}`
      : `${homeUrl}/fx-tran?orderRef=${currentOrderRef}&paymentStatus=${STATUS_FAIL}`;

    if (urlRedirect) {
      window.location.href = urlRedirect;
      return;
    }
    window.location.href = failPath;
    handleOnCloseFormPaymentByDeposit();
  };
  const handleOnPayByDepositProcess = (
    urlRedirect,
    transactionInformation,
    handleOnCloseFormPaymentByDeposit
  ) => {
    handleOnPayByDeposit({
      urlRedirect,
      transactionInformation,
      handleOnCloseFormPaymentByDeposit,
    });
  };

  const paymentMintProcess = async (orderRef, tokenTransaction, email) => {
    const homeUrl = `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;
    const successPath = isFxAsGuest
      ? `${homeUrl}/fx-tran?as=${AS_GUEST}&orderRef=${orderRef}&paymentStatus=${STATUS_SUCCESS}&email=${email}`
      : `${homeUrl}/fx-tran?orderRef=${orderRef}&paymentStatus=${STATUS_SUCCESS}`;
    const failPath = isFxAsGuest
      ? `${homeUrl}/fx-tran?as=${AS_GUEST}&orderRef=${orderRef}&paymentStatus=${STATUS_FAIL}&email=${email}`
      : `${homeUrl}/fx-tran?orderRef=${orderRef}&paymentStatus=${STATUS_FAIL}`;

    try {
      const { data } = await api.fxProcessMint(orderRef, tokenTransaction);
      if (data) {
        const { success } = data;

        setTokenTransaction();
        setPending(false);

        if (success) {
          window.location.href = successPath;
        } else {
          window.location.href = failPath;
        }
      }
    } catch (error) {
      checkMaintenance(error);
      LogUtil.error('paymentMintProcess', 'error', error);
      setTokenTransaction();
      setPending(false);
      window.location.href = failPath;
    }
  };

  return {
    handleOnPayProcess,
    handleOnPayByDepositProcess,
    paymentMintProcess,
    homeUrl,
  };
};

export default useFxPayment;
